import { combineEpics, Epic } from 'redux-observable';
import { from, of, EMPTY } from 'rxjs';
import {
  delay,
  switchMap,
  map,
  mergeMap,
  startWith,
  catchError,
  filter
} from 'rxjs/operators';
import { isOfType } from 'typesafe-actions';
import { Position, Toaster, Intent } from '@blueprintjs/core';

import {
  ReportResultActions,
  ExecuteReportAction,
  ReportResultActionTypes,
  executedReport,
  executingReport,
  executingReportFailed,
  checkReportExecution,
  loadedReportResult,
  ReRunReportAction,
  reRunningReport,
  reRunReportFailed,
} from '../actions/reportResultActions';
import {setShowSubscriptionDialog} from '../actions/uiActions';
import { IState } from '../reducers';
import {
  executeReport,
  reRunReport,
} from '../../services/reportApi';
import {getNoteflowExecution} from '../../services/noteflowApi';

export const reportResultToaster = Toaster.create({
  className: 'report-result-toaster',
  position: Position.TOP
});

/*
 * TODO - merge this into `noteflowEpics.ts`
 */
const executeReportEpic: Epic<ReportResultActions, ReportResultActions, IState, any> = (
  action$: any,
  state$: any,
  {dispatch}: any
) =>
  action$.pipe(
    filter(isOfType(ReportResultActionTypes.EXECUTE_REPORT)),
    switchMap((action: ExecuteReportAction) =>
      from(executeReport(action.payload.uid, action.payload.options)).pipe(
        map((response: any) => {
          const runId = response?.result?.noteflow_run_uid;

          return checkReportExecution(action.payload.uid, runId);
        }),
        startWith(executingReport()),
        catchError((err: any) => {
          const message = err.response?.data?.message || "";

          if (message.indexOf("No subscription found") >= 0) {
            dispatch(setShowSubscriptionDialog(true));
          } else {
            reportResultToaster.show({
              message: `An error occurred while executing the report: ${message}.`,
              intent: Intent.DANGER
            });
          }

          return of(executingReportFailed(action.payload.uid));
        })
      )
    )
  );

const reRunReportEpic: Epic<ReportResultActions, ReportResultActions, IState, any> = (
  action$: any,
  state$: any,
  {dispatch}: any
) =>
  action$.pipe(
    filter(isOfType(ReportResultActionTypes.RE_RUN_REPORT)),
    switchMap((action: ReRunReportAction) =>
      from(reRunReport(
        action.payload.uid,
        action.payload.flowRunId,
        action.payload.taskRunIds,
        action.payload.taskRunParentId,
      )).pipe(
        map((response: any) => {
          const runId = response?.result?.noteflow_run_uid;

          return checkReportExecution(action.payload.uid, runId);
        }),
        startWith(reRunningReport()),
        catchError((err: any) => {
          const message = err.response?.data?.message || "";

          if (message.indexOf("No subscription found") >= 0) {
            dispatch(setShowSubscriptionDialog(true));
          } else {
            reportResultToaster.show({
              message: `An error occurred while re-running the report: ${message}.`,
              intent: Intent.DANGER
            });
          }

          return of(reRunReportFailed(action.payload.uid));
        })
      )
    )
  );

const CHECK_FINISH_STATUS = ['success', 'failed', 'cancelled']
const checkReportExecutionEpic: Epic<ReportResultActions, ReportResultActions, IState, any> = (
  action$: any,
  state$: any,
  { dispatch }: any
) =>
  action$.pipe(
    filter(isOfType(ReportResultActionTypes.CHECK_REPORT_EXECUTION)),
    mergeMap(
      (action: any) => {
        let delayTime = 2000;

        if (action.payload.counter > 0 && state$.value.reportResult.executionStatus === null) {
          /*
           * if report execution status were unset, no need to check for results anymore
           */
          return EMPTY;
        }

        return from (getNoteflowExecution(action.payload.resultId, false))
          .pipe(
            delay(delayTime),
            map(({ result }: any) => {
              const finished =
                (result && result?.status && CHECK_FINISH_STATUS.indexOf(result?.status) >= 0) || false;

              if (action.payload.counter >= 900 || !result || finished) {
                /*
                 * if no execution or we checked more than N times => Timeout
                 * or execution finished
                 */

                if (finished) {
                  dispatch(executedReport(action.payload.uid));
                } else {
                  dispatch(executingReportFailed(action.payload.uid));
                }
                const defaultResult: any = {
                  uid: null,
                  status: 'failed',
                };
                return loadedReportResult(action.payload.uid, result || defaultResult);
              }

              return checkReportExecution(
                action.payload.uid,
                action.payload.resultId,
                action.payload.counter + 1
              );
            }),
            catchError((err: any) => {
              const message = err.response?.data?.message || "";

              reportResultToaster.show({
                message: `An error occurred while loading executions: ${message}.`,
                intent: Intent.DANGER
              });

              return of(executingReportFailed(action.payload.uid));
            }),
          )
      }
    )
  );

export default combineEpics(
  executeReportEpic,
  reRunReportEpic,
  checkReportExecutionEpic,
);
